import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Alert,
    Badge,
    Container,
    Group,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Text,
    Title,
} from "@mantine/core";
import "../Practice/PracticePlannerPreviewPage.css";

interface PlannerLocation {
    practicePlanLocationId: number;
    practiceLocationId: number;
    locationName: string;
    numberOfCourts: number;
}

interface PlannerBooking {
    practicePlanBookingId: number;
    practiceId: number;
    country: string;
    practiceLocationId: number;
    locationName: string;
    startTime: string;
    endTime: string;
    courtFrom?: number;
    courtTo?: number;
    numberOfCourts: number;
    numberOfPlayers?: number;
    note?: string;
}

interface PlannerDto {
    practicePlanId: number;
    eventId: number;
    practiceDate: string;
    status: string;
    locations: PlannerLocation[];
    requests: any[];
    bookings: PlannerBooking[];
}

const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 62.5;

const BOARD_START_HOUR = 8;
const BOARD_END_HOUR = 22;

const BOARD_START_MINUTES =
    BOARD_START_HOUR * 60;

// ----------------------------------------------------------
// LOCATION COLORS
// ----------------------------------------------------------

const LOCATION_COLORS = [
    {
        background: "#e7f5ff",
        border: "#339af0",
    },
    {
        background: "#d0ebff",
        border: "#228be6",
    },
    {
        background: "#a5d8ff",
        border: "#1c7ed6",
    },
    {
        background: "#74c0fc",
        border: "#1971c2",
    },
    {
        background: "#dbeafe",
        border: "#3b82f6",
    },
    {
        background: "#bfdbfe",
        border: "#2563eb",
    },
    {
        background: "#93c5fd",
        border: "#1d4ed8",
    },
    {
        background: "#60a5fa",
        border: "#1e40af",
    },
];

const timeToMinutes = (time: string) => {
    if (!time) return 0;

    const parts = time.split(":");

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    return hours * 60 + minutes;
};

const getBookingTop = (startTime: string) => {
    const startMinutes =
        timeToMinutes(startTime);

    return (
        ((startMinutes -
            BOARD_START_MINUTES) /
            SLOT_MINUTES) *
        SLOT_HEIGHT
    );
};

const getBookingHeight = (
    startTime: string,
    endTime: string
) => {
    const startMinutes =
        timeToMinutes(startTime);

    const endMinutes =
        timeToMinutes(endTime);

    const duration =
        endMinutes - startMinutes;

    return (
        (duration / SLOT_MINUTES) *
        SLOT_HEIGHT
    );
};

const getBookingCourtFrom = (
    booking: PlannerBooking
) => {
    return booking.courtFrom ?? 1;
};

const getBookingCourtTo = (
    booking: PlannerBooking
) => {
    return (
        booking.courtTo ??
        (
            getBookingCourtFrom(booking) +
            booking.numberOfCourts -
            1
        )
    );
};

const bookingOccupiesCourt = (
    booking: PlannerBooking,
    courtNumber: number
) => {
    const courtFrom =
        getBookingCourtFrom(booking);

    const courtTo =
        getBookingCourtTo(booking);

    return (
        courtNumber >= courtFrom &&
        courtNumber <= courtTo
    );
};

export default function PracticePlannerPreviewPage() {
    const { planId } = useParams();

    const practicePlanId =
        Number(planId);

    const apiBase =
        process.env.REACT_APP_API_URL;

    const [plan, setPlan] =
        useState<PlannerDto | null>(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    useEffect(() => {
        const loadPlan = async () => {
            if (
                !practicePlanId ||
                practicePlanId <= 0
            ) {
                setError(
                    "Invalid plan ID."
                );

                setLoading(false);

                return;
            }

            try {
                setLoading(true);
                setError("");

                const response =
                    await fetch(
                        `${apiBase}/api/practice-planner/public/${practicePlanId}`
                    );

                if (!response.ok) {
                    if (
                        response.status === 404
                    ) {
                        throw new Error(
                            "This practice plan does not exist or has not been published."
                        );
                    }

                    throw new Error(
                        "Could not load the practice plan."
                    );
                }

                const data =
                    await response.json();

                setPlan(data);
            } catch (err) {
                console.error(err);

                setError(
                    err instanceof Error
                        ? err.message
                        : "An error occurred."
                );
            } finally {
                setLoading(false);
            }
        };

        loadPlan();
    }, [
        apiBase,
        practicePlanId,
    ]);

    const courts = useMemo(() => {
        if (!plan) return [];

        const result: {
            locationId: number;
            locationName: string;
            courtNumber: number;
            color: {
                background: string;
                border: string;
            };
        }[] = [];

        plan.locations.forEach(
            (location, locationIndex) => {
                const color =
                    LOCATION_COLORS[
                    locationIndex %
                    LOCATION_COLORS.length
                    ];

                for (
                    let court = 1;
                    court <=
                    location.numberOfCourts;
                    court++
                ) {
                    result.push({
                        locationId:
                            location.practiceLocationId,

                        locationName:
                            location.locationName,

                        courtNumber:
                            court,

                        color,
                    });
                }
            }
        );

        return result;
    }, [plan]);

    const timeSlots = useMemo(() => {
        const slots: number[] = [];

        for (
            let minutes =
                BOARD_START_HOUR * 60;
            minutes <=
            BOARD_END_HOUR * 60;
            minutes += SLOT_MINUTES
        ) {
            slots.push(minutes);
        }

        return slots;
    }, []);

    if (loading) {
        return (
            <Container size="xl">
                <Stack
                    align="center"
                    justify="center"
                    py="xl"
                >
                    <Loader size="sm" />

                    <Text size="sm">
                        Loading practice plan...
                    </Text>
                </Stack>
            </Container>
        );
    }

    if (error || !plan) {
        return (
            <Container size="xl">
                <Alert
                    color="red"
                    title="Could not load practice plan"
                >
                    {error ||
                        "The practice plan could not be found."}
                </Alert>
            </Container>
        );
    }

    const boardHeight =
        ((BOARD_END_HOUR -
            BOARD_START_HOUR) *
            60 /
            SLOT_MINUTES) *
        SLOT_HEIGHT;

    return (
        <Container
            size="100%"
            px="sm"
            py="sm"
        >
            <Stack gap="sm">

                {/* HEADER */}
                <Paper
                    withBorder
                    p="md"
                >
                    <Group
                        justify="center"
                        align="center"
                    >
                        <div
                            style={{
                                textAlign: "center",
                            }}
                        >
                            <Title order={2}>
                                Practice Plan
                            </Title>

                            <Title
                                order={4}
                                mt={4}
                                fw={600}
                            >
                                {new Date(
                                    plan.practiceDate
                                ).toLocaleDateString(
                                    "en-GB",
                                    {
                                        weekday: "long",
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    }
                                )}
                            </Title>
                        </div>

                        <Badge
                            color="green"
                            size="lg"
                        >
                            Published
                        </Badge>
                    </Group>
                </Paper>

                {/* BOARD */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    <Paper
                        withBorder
                        p="xs"
                        style={{
                            maxWidth: "100%",
                        }}
                    >
                        <ScrollArea
                            type="auto"
                            offsetScrollbars
                        >
                            <div
                                className="practice-planner-board"
                                style={{
                                    minWidth:
                                        Math.max(
                                            625,
                                            courts.length *
                                            112.5 +
                                            50
                                        ),
                                }}
                            >
                                {/* COURT HEADERS */}
                                <div
                                    className="practice-planner-board-header"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            `50px repeat(${courts.length}, 112.5px)`,
                                    }}
                                >
                                    <div />

                                    {courts.map(
                                        (
                                            court
                                        ) => (
                                            <div
                                                key={`${court.locationId}-${court.courtNumber}`}
                                                style={{
                                                    padding:
                                                        "5px 2px",
                                                    borderLeft:
                                                        "1px solid var(--mantine-color-gray-3)",
                                                    borderTop:
                                                        `3px solid ${court.color.border}`,
                                                    background:
                                                        court.color.background,
                                                    textAlign:
                                                        "center",
                                                }}
                                            >
                                                <Text
                                                    fw={600}
                                                    size="xs"
                                                    truncate
                                                >
                                                    {
                                                        court.locationName
                                                    }
                                                </Text>

                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Court{" "}
                                                    {
                                                        court.courtNumber
                                                    }
                                                </Text>
                                            </div>
                                        )
                                    )}
                                </div>

                                {/* BOARD BODY */}
                                <div
                                    style={{
                                        display:
                                            "grid",
                                        gridTemplateColumns:
                                            `50px repeat(${courts.length}, 112.5px)`,
                                    }}
                                >
                                    {/* TIME COLUMN */}
                                    <div
                                        style={{
                                            position:
                                                "relative",
                                            height:
                                                `${boardHeight}px`,
                                        }}
                                    >
                                        {timeSlots.map(
                                            (
                                                minutes
                                            ) => {
                                                const top =
                                                    ((minutes -
                                                        BOARD_START_MINUTES) /
                                                        SLOT_MINUTES) *
                                                    SLOT_HEIGHT;

                                                return (
                                                    <div
                                                        key={
                                                            minutes
                                                        }
                                                        style={{
                                                            position:
                                                                "absolute",
                                                            top,
                                                            left: 0,
                                                            right: 0,
                                                            height:
                                                                `${SLOT_HEIGHT}px`,
                                                            borderTop:
                                                                "1px solid var(--mantine-color-gray-3)",
                                                            padding:
                                                                "2px",
                                                        }}
                                                    >
                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                        >
                                                            {String(
                                                                Math.floor(
                                                                    minutes /
                                                                    60
                                                                )
                                                            ).padStart(
                                                                2,
                                                                "0"
                                                            )}
                                                            :
                                                            {String(
                                                                minutes %
                                                                60
                                                            ).padStart(
                                                                2,
                                                                "0"
                                                            )}
                                                        </Text>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>

                                    {/* COURT COLUMNS */}
                                    {courts.map(
                                        (
                                            court
                                        ) => {
                                            const courtBookings =
                                                plan.bookings.filter(
                                                    (
                                                        booking
                                                    ) =>
                                                        booking.practiceLocationId ===
                                                        court.locationId &&
                                                        bookingOccupiesCourt(
                                                            booking,
                                                            court.courtNumber
                                                        )
                                                );

                                            return (
                                                <div
                                                    key={`${court.locationId}-${court.courtNumber}`}
                                                    style={{
                                                        position:
                                                            "relative",
                                                        height:
                                                            `${boardHeight}px`,
                                                        borderLeft:
                                                            "1px solid var(--mantine-color-gray-3)",
                                                        background:
                                                            court.color.background,
                                                    }}
                                                >
                                                    {/* TIME GRID */}
                                                    {timeSlots.map(
                                                        (
                                                            minutes
                                                        ) => (
                                                            <div
                                                                key={
                                                                    minutes
                                                                }
                                                                style={{
                                                                    position:
                                                                        "absolute",
                                                                    top:
                                                                        ((minutes -
                                                                            BOARD_START_MINUTES) /
                                                                            SLOT_MINUTES) *
                                                                        SLOT_HEIGHT,
                                                                    left: 0,
                                                                    right: 0,
                                                                    height:
                                                                        `${SLOT_HEIGHT}px`,
                                                                    borderTop:
                                                                        "1px solid var(--mantine-color-gray-2)",
                                                                    pointerEvents:
                                                                        "none",
                                                                }}
                                                            />
                                                        )
                                                    )}

                                                    {/* BOOKINGS */}
                                                    {courtBookings.map(
                                                        (
                                                            booking
                                                        ) => {
                                                            const top =
                                                                getBookingTop(
                                                                    booking.startTime
                                                                );

                                                            const height =
                                                                getBookingHeight(
                                                                    booking.startTime,
                                                                    booking.endTime
                                                                );

                                                            return (
                                                                <Paper
                                                                    key={
                                                                        booking.practicePlanBookingId
                                                                    }
                                                                    shadow="xs"
                                                                    withBorder
                                                                    p={5}
                                                                    style={{
                                                                        position:
                                                                            "absolute",
                                                                        top,
                                                                        left:
                                                                            2,
                                                                        right:
                                                                            2,
                                                                        height,
                                                                        overflow:
                                                                            "hidden",
                                                                        zIndex:
                                                                            2,
                                                                        borderLeft:
                                                                            `3px solid ${court.color.border}`,
                                                                        background:
                                                                            "#ffffff",
                                                                    }}
                                                                >
                                                                    <Text
                                                                        fw={
                                                                            700
                                                                        }
                                                                        size="xs"
                                                                        truncate
                                                                    >
                                                                        {
                                                                            booking.country
                                                                        }
                                                                    </Text>

                                                                    <Text
                                                                        size="xs"
                                                                        c="dimmed"
                                                                        truncate
                                                                    >
                                                                        {
                                                                            booking.startTime
                                                                        }{" "}
                                                                        -
                                                                        {
                                                                            booking.endTime
                                                                        }
                                                                    </Text>

                                                                    {booking.note && (
                                                                        <Text
                                                                            size="xs"
                                                                            mt={
                                                                                2
                                                                            }
                                                                            lineClamp={
                                                                                2
                                                                            }
                                                                        >
                                                                            {
                                                                                booking.note
                                                                            }
                                                                        </Text>
                                                                    )}
                                                                </Paper>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        </ScrollArea>
                    </Paper>
                </div>
            </Stack>
        </Container>
    );
}